/**
 * The unplaced tray (spec--pdfmarkuptool-image-markup 4.3 "The Photo tray tool", flow 5(f); D-D, D-L; slices I8,
 * I8b): photos ingested NOW and placed LATER, with the Photo tray tool. "Add photos" picks many; each is stored by
 * Ingest (its bytes are durable in the AssetStore before it reaches the tray, M1/M2) and lands here. The tool's panel
 * shows them; a thumbnail dragged onto a sheet becomes a `photo-pin` or an `image`, and a placed one dragged back onto
 * the panel returns to the tray.
 *
 * Pure. The tray is a list of entries whose ORDER is a function of the entries alone ({@link orderTray}): dated
 * photos by `capturedAt` (I1: EXIF DateTimeOriginal, else the file-date rule), then the undated ones in pick order.
 *
 * **The VISIBLE tray is derived** ({@link visibleTray}): the stored entries whose asset no markup of the document
 * references. Placing writes only the markup and restoring only adds the entry if it is absent before the markup is
 * deleted, so undo / redo of a place or a restore need nothing from the tray. Entries of placed photos stay stored;
 * at the {@link TRAY_MAX_ENTRIES} cap, Add photos first drops entries whose photo is placed, oldest first.
 *
 * Where a tray is stored is the {@link TrayStore} seam: a local workspace keeps it on its workspace record
 * (LocalWorkspaceService); a cloud session on the user's own `trays/{uid}` record under the document (I9 round 2).
 */
import type { IngestItem } from './ingest';

/** What a tray photo places: Ingest's item, plus the caption it had on the pin it was restored from (Q-R). */
export type TrayItem = IngestItem & { caption?: string };

/** One unplaced photo: Ingest's item (its asset already stored) plus its place in the tray. */
export interface TrayEntry extends IngestItem {
    /** Pick order: increases across batches, so undated photos keep the order they were picked in. */
    pick: number;
    /**
     * The caption of the pin (or image) this photo was restored from (owner question Q-R): re-applied at its next
     * placement, so dictated text is never lost silently once Undo is gone (a reload).
     */
    caption?: string;
}

/** A tray record of a cloud session (one per user per document): its entries and a write counter. */
export interface StoredTray { entries: TrayEntry[]; rev: number }

/** Where a tray lives (local: the workspace record; cloud: a per-user record under the document). */
export interface TrayStore {
    load(): Promise<TrayEntry[]>;
    /** Read-modify-write in one step: `fn` gets the stored tray and returns the next one. Resolves with it. */
    update(fn: (tray: TrayEntry[]) => TrayEntry[]): Promise<TrayEntry[]>;
    /** Called after every change of the stored tray. */
    subscribe(listener: () => void): () => void;
}

const dated = (e: TrayEntry) => typeof e.capturedAt === 'number' && Number.isFinite(e.capturedAt);

/** The tray order (see the module doc). Returns a new array; never mutates. */
export function orderTray(tray: readonly TrayEntry[]): TrayEntry[] {
    return [...tray].sort((a, b) => {
        const da = dated(a), db = dated(b);
        if (da !== db) return da ? -1 : 1;
        if (da && db && a.capturedAt !== b.capturedAt) return a.capturedAt! - b.capturedAt!;
        return a.pick - b.pick;
    });
}

/** The next pick counter: above every pick in the tray. */
function nextPick(tray: readonly TrayEntry[]): number {
    return tray.reduce((m, e) => Math.max(m, e.pick), 0) + 1;
}

/** Every asset a markup of the document holds (its `media` items): the photos that are PLACED. */
export function placedAssets(markups: ReadonlyArray<{ media?: Record<string, { asset: string }> }>): Set<string> {
    const out = new Set<string>();
    for (const m of markups) for (const it of Object.values(m.media ?? {})) out.add(it.asset);
    return out;
}

/** The tray the panel shows: the stored entries whose photo is not placed, in tray order. */
export function visibleTray(tray: readonly TrayEntry[], placed: ReadonlySet<string>): TrayEntry[] {
    return orderTray(tray.filter((e) => !placed.has(e.asset)));
}

/**
 * Add Ingest's items, in pick order. A photo already in the tray (same asset) is not added twice: `duplicates`
 * counts the visible ones, `placed` the ones already on the drawing (the caller says so; never silent). At the cap,
 * entries whose photo is placed (`placed`) are dropped first, oldest pick first; `full` counts what still found no
 * room.
 */
export function addToTray(
    tray: readonly TrayEntry[],
    items: readonly IngestItem[],
    placed: ReadonlySet<string> = new Set(),
): { tray: TrayEntry[]; added: number; duplicates: number; placed: number; full: number } {
    const have = new Set(tray.map((e) => e.asset));
    let n = nextPick(tray);
    const out = [...tray];
    // Prunable entries (photo placed), oldest pick first.
    const prunable = out.filter((e) => placed.has(e.asset)).sort((a, b) => a.pick - b.pick);
    let added = 0;
    let duplicates = 0;
    let onDrawing = 0;
    for (const it of items) {
        if (have.has(it.asset)) {
            if (placed.has(it.asset)) onDrawing++;
            else duplicates++;
            continue;
        }
        if (out.length >= TRAY_MAX_ENTRIES) {
            const drop = prunable.shift();
            if (!drop) continue;
            out.splice(out.indexOf(drop), 1);
            have.delete(drop.asset);
        }
        have.add(it.asset);
        out.push({ ...it, pick: n++ });
        added++;
    }
    return { tray: orderTray(out), added, duplicates, placed: onDrawing, full: items.length - added - duplicates - onDrawing };
}

/**
 * Restore: the photo of a pin (or image) dragged back onto the panel. Its entry is added when absent (a camera pin's
 * photo never was in the tray); a stored entry keeps its place. Either way the entry's caption becomes the pin's
 * (none: no caption). Room, heading and seq are not kept: the next placement resolves them again.
 */
export function restoreToTray(tray: readonly TrayEntry[], item: TrayItem): TrayEntry[] {
    const { caption, ...base } = item;
    const withCaption = (e: TrayEntry): TrayEntry => {
        const { caption: _old, ...rest } = e;
        return caption ? { ...rest, caption } : rest;
    };
    const at = tray.findIndex((e) => e.asset === item.asset);
    if (at >= 0) return orderTray(tray.map((e, i) => (i === at ? withCaption(e) : e)));
    return orderTray([...tray, withCaption({ ...base, pick: nextPick(tray) })]);
}

/** At most this many photos wait in one tray (a site visit is 50-600 photos, spec U7; ~200 B each in a stored record). */
export const TRAY_MAX_ENTRIES = 1000;

const HEX64 = /^[0-9a-f]{64}$/;
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
/** The media item's caption limit (types/schemas.ts MediaItemSchema). */
const CAPTION_MAX = 500;

/**
 * A tray as it may be STORED (and as it is read back): well-formed entries only, no `undefined` anywhere (Firestore
 * refuses it), at most {@link TRAY_MAX_ENTRIES}, in tray order. A malformed entry (another client's bug, a hand edit)
 * is dropped, never thrown on: the rest of the tray still places. An I8 `skip` is not carried (v3.2: no Skip).
 */
export function cleanTray(raw: unknown): TrayEntry[] {
    if (!Array.isArray(raw)) return [];
    const out: TrayEntry[] = [];
    for (const v of raw) {
        const e = v as Partial<TrayEntry> | null;
        if (!e || typeof e.asset !== 'string' || !HEX64.test(e.asset) || e.mime !== 'image/jpeg') continue;
        if (!finite(e.w) || !finite(e.h) || !finite(e.pick) || typeof e.source?.kind !== 'string') continue;
        out.push({
            asset: e.asset, mime: 'image/jpeg', w: e.w, h: e.h, pick: e.pick,
            ...(finite(e.capturedAt) ? { capturedAt: e.capturedAt } : {}),
            ...(typeof e.caption === 'string' && e.caption ? { caption: e.caption.slice(0, CAPTION_MAX) } : {}),
            source: typeof e.source.url === 'string' ? { kind: e.source.kind, url: e.source.url } : { kind: e.source.kind },
        });
    }
    return orderTray(out).slice(0, TRAY_MAX_ENTRIES);
}

/** What a tray entry places (the draft builder's item): Ingest's item and its kept caption, without the tray fields. */
export function trayItem(e: TrayEntry): TrayItem {
    const { pick: _p, ...item } = e;
    return item;
}
